import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import { useSeo } from "@/hooks/useSeo";
import { getCaseById, getRelatedCases } from "@/utils/cases";

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getCaseById(id) : undefined;

  const related = item ? getRelatedCases(item, 3) : [];

  useSeo({
    title: item ? `${item.title}｜黑暗爆料` : "档案未找到｜黑暗爆料",
    description: item
      ? `${item.summary}（${item.categoryLabel} · ${item.author}）`
      : "该调查档案不存在或已调整地址。",
    keywords: item
      ? ["黑暗爆料", item.categoryLabel, ...item.tags].join(",")
      : "黑暗爆料,调查档案",
    path: `/cases/${id ?? ""}`,
    type: "article",
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.summary,
            datePublished: item.date,
            dateModified: item.updated,
            articleSection: item.categoryLabel,
            keywords: item.tags.join(","),
            author: { "@type": "Organization", name: "黑暗爆料" },
            publisher: { "@type": "Organization", name: "黑暗爆料" },
            mainEntityOfPage: `https://i2785.cn/cases/${item.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://i2785.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "调查档案",
                item: "https://i2785.cn/cases",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: item.title,
                item: `https://i2785.cn/cases/${item.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="pt-16 md:pt-[72px]">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-24 text-center">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-md bg-background-100 text-3xl text-foreground-400 mb-5">
              <i className="ri-file-unknow-line" aria-hidden="true"></i>
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              没有找到这份档案
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              它可能已经调整了地址，或者从未存在。
            </p>
            <Link
              to="/cases"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回档案库
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="pt-16 md:pt-[72px]">
        <header className="bg-foreground-950 text-background-50">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-10 md:py-14">
            <div className="[&_*]:!text-background-300">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "调查档案", to: "/cases" },
                  { label: item.categoryLabel, to: `/cases?category=${item.category}` },
                ]}
              />
            </div>

            <p className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {item.categoryLabel}
            </p>

            <h1 className="mt-5 font-heading text-2xl md:text-4xl lg:text-[2.7rem] font-bold leading-[1.25] text-background-50 max-w-4xl">
              {item.title}
            </h1>

            <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
              {item.summary}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-background-300">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true"></i>
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                发布 {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" aria-hidden="true"></i>
                最近修订 {item.updated}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                {item.readTime}阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line" aria-hidden="true"></i>
                {item.heat.toLocaleString()} 阅读
              </span>
            </div>
          </div>
        </header>

        <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24 space-y-5">
                <nav
                  aria-label="正文目录"
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-list-unordered text-primary-600" aria-hidden="true"></i>
                    正文目录
                  </h2>
                  <ol className="space-y-2.5">
                    {item.sections.map((section, index) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className="flex gap-2 text-xs text-foreground-600 leading-relaxed hover:text-primary-600 transition-colors"
                        >
                          <span className="font-heading text-primary-500">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>

                <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                    <i className="ri-shield-check-line text-primary-600" aria-hidden="true"></i>
                    快速避坑要点
                  </h2>
                  <ul className="space-y-2">
                    {item.tips.slice(0, 3).map((tip) => (
                      <li key={tip} className="flex gap-2 text-xs text-foreground-700 leading-relaxed">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 order-1 lg:order-2">
              <figure className="rounded-lg overflow-hidden border border-background-200 mb-10">
                <div className="aspect-[16/9] w-full bg-secondary-900">
                  <img
                    src={item.cover}
                    alt={item.coverAlt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="px-4 py-3 text-xs text-foreground-500 bg-background-100">
                  {item.coverAlt}
                </figcaption>
              </figure>

              <div className="space-y-10">
                {item.sections.map((section, index) => (
                  <section key={section.id} id={section.id} className="scroll-mt-28">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                      <span className="text-primary-500 mr-2">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph.slice(0, 24)}
                        className="mt-4 text-sm md:text-base text-foreground-700 leading-[1.9]"
                      >
                        {paragraph}
                      </p>
                    ))}
                    {section.points && (
                      <ul className="mt-5 space-y-2.5 rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                        {section.points.map((point) => (
                          <li
                            key={point}
                            className="flex gap-2.5 text-sm text-foreground-700 leading-relaxed"
                          >
                            <i
                              className="ri-arrow-right-s-fill text-accent-600 mt-0.5"
                              aria-hidden="true"
                            ></i>
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-alert-line text-primary-600" aria-hidden="true"></i>
                    风险提示
                  </h2>
                  <ul className="space-y-3">
                    {item.risks.map((risk) => (
                      <li key={risk} className="flex gap-2.5 text-sm text-foreground-700 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 shrink-0" aria-hidden="true"></span>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-accent-300 bg-accent-50 p-5 md:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-lightbulb-flash-line text-accent-700" aria-hidden="true"></i>
                    避坑要点
                  </h2>
                  <ul className="space-y-3">
                    {item.tips.map((tip) => (
                      <li key={tip} className="flex gap-2.5 text-sm text-foreground-700 leading-relaxed">
                        <i className="ri-check-line text-accent-700 mt-0.5" aria-hidden="true"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {item.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/cases?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-md bg-secondary-50 text-secondary-900 text-xs hover:bg-secondary-100 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
                <Link
                  to="/cases"
                  className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回档案库
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100 border-t border-background-200">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-14 md:py-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              继续读下去
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
              {related.map((rel) => (
                <CaseCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}